import { lazy, ComponentType, LazyExoticComponent } from 'react';

/**
 * Robust Lazy Loader with exponential backoff retry mechanism
 * Prevents "Failed to fetch dynamically imported module" errors during server restarts or transient network drops.
 */
export function lazyWithRetry<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T } | { [key: string]: any }>,
  retries = 3
): LazyExoticComponent<T> {
  return lazy(async () => {
    let lastError: any;
    
    for (let attempt = 0; attempt < retries; attempt++) {
      try {
        const module = await factory();
        if (module && 'default' in module && module.default) {
          return { default: module.default as T };
        }
        const firstExport = Object.values(module || {}).find((v) => typeof v === 'function');
        if (firstExport) {
          return { default: firstExport as T };
        }
        return { default: ((module as any)?.default || module) as T };
      } catch (error: any) {
        lastError = error;
        // Wait with progressive backoff (500ms, 1200ms, 2500ms)
        const delay = Math.min(500 * Math.pow(2, attempt), 3000);
        await new Promise((resolve) => setTimeout(resolve, delay));
      }
    }

    // If all retries fail, check if this is a chunk fetch error from a server reload
    const isChunkLoadError = 
      lastError?.message?.includes('Failed to fetch dynamically imported module') ||
      lastError?.name === 'ChunkLoadError' ||
      lastError?.message?.includes('dynamically imported module') ||
      lastError?.message?.includes('Loading chunk');

    if (isChunkLoadError && typeof window !== 'undefined') {
      const storageKey = 'djp_chunk_reload_ts';
      const lastReload = sessionStorage.getItem(storageKey);
      const now = Date.now();
      
      // If we haven't auto-reloaded in the last 15 seconds, reload cleanly to get the latest chunk bundle
      if (!lastReload || now - parseInt(lastReload, 10) > 15000) {
        sessionStorage.setItem(storageKey, String(now));
        window.location.reload();
        // Return an unresolving promise so React doesn't crash before reload executes
        return new Promise<{ default: T }>(() => {});
      }
    }

    throw lastError;
  });
}

